import { AxiosRequestConfig } from 'axios';
import { GenericApiResponse } from '../../shared/interfaces/generics';
import { appConfig } from '../../request/app.config';
import {
  BACKGROUND_NETWORK_REQ_UI_OPTIONS,
  DEFAULT_NETWORK_REQ_OPTIONS,
  NETWORK_REQ_OPTIONS_WITH_TOAST
} from '../../request/constants/request-options';
import { courierService } from '../../request/services/courier.service';
import {
  RawIntegration,
  RawIntegrationBannerResponse,
  RawPipelineReviewContent,
  RawPipelineReviewSelectionResponse,
  StandardPipelineInfo
} from '../../shared/interfaces/integration';
import { NetworkRequestOptions } from '../../request/interfaces/request-options';
import {
  DestinationPartitionKeyValidationRequest,
  DestinationPrefixValidationRequest,
  RawDestinationType
} from '../../shared/interfaces/destination';
import { RawEntityDisplayConfig } from '../../shared/interfaces/entity-display-config';
import { RawSourceType } from '../../shared/interfaces/source';
import { PipelineConfigPayload } from '../PipelineSettings/interfaces/pipeline-settings-form-state';
import { PipelineSettingsFeaturesData } from '../interfaces/pipeline-settings-config';
import { EntityDefaultName } from '../../template-form/interfaces/template-raw-form';

export const PipelineAPI = {
  integrationsURL: appConfig.getIntegrationsURL(),
  upgradeIntegrationURL: appConfig.getV1IntegrationURL(),
  objectsConfigURL: `${appConfig.getObjectsURL()}/configuration`,

  createIntegration(options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.integrationsURL}/create`;

    return courierService.post<GenericApiResponse<{ id: number; seq_id: number; team_id: number }>>(
      requestURL,
      {},
      options
    );
  },

  getDefaultPipelineName(
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ): Promise<GenericApiResponse<EntityDefaultName>> {
    const requestURL = `${this.integrationsURL}/default-name`;

    return courierService.get<GenericApiResponse<EntityDefaultName>>(requestURL, options);
  },

  getIntegration(integrationId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.integrationsURL}/${integrationId}`;

    return courierService.get<GenericApiResponse<RawIntegration>>(requestURL, options);
  },

  getIntegrationStatus(integrationId: number) {
    const requestURL = `${this.integrationsURL}/${integrationId}/status?include_deleted=true`;

    return courierService.get<GenericApiResponse<RawIntegration['status']>>(requestURL);
  },

  getIntegrations(options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = this.integrationsURL;

    return courierService.get<GenericApiResponse<RawIntegration[]>>(requestURL, {
      ...options,
      networkOptions: {
        params: {
          include_deleted: false
        }
      }
    });
  },

  editIntegrationConfig(
    integrationId: number,
    config: PipelineConfigPayload,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ) {
    const requestURL = `${this.integrationsURL}/${integrationId}/edit/config`;

    return courierService.post<GenericApiResponse<RawIntegration>>(requestURL, config, options);
  },

  configureSource(integrationId: number, sourceId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.integrationsURL}/${integrationId}/edit/source`;
    const params = {
      source_id: sourceId
    };

    return courierService.post<GenericApiResponse<RawIntegration>>(requestURL, params, options);
  },

  configureDestination(
    integrationId: number,
    destinationId: number,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ) {
    const requestURL = `${this.integrationsURL}/${integrationId}/edit/destination`;
    const params = {
      destination_id: destinationId
    };

    return courierService.post<GenericApiResponse<RawIntegration>>(requestURL, params, options);
  },

  validateIntegrationName(pipelineName: string, config = {}, extraParams = {}) {
    const requestURL = `${this.integrationsURL}/validate/pipeline-name`;

    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    const params = {
      pipeline_name: pipelineName,
      ...extraParams
    };

    return courierService.post<GenericApiResponse<{ valid: boolean; reason?: string }>>(
      requestURL,
      params,
      options
    );
  },

  validateDestinationPrefix(
    value: DestinationPrefixValidationRequest,
    config = {},
    extraParams = {}
  ) {
    const requestURL = `${this.integrationsURL}/validate/destination-prefix/${value.destinationType}`;

    const params = {
      destination_prefix: value.destinationPrefix,
      ...extraParams
    };

    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return courierService.post<GenericApiResponse<{ valid: boolean; reason: string }>>(
      requestURL,
      params,
      options
    );
  },

  validateDestinationPartitionKey(value: DestinationPartitionKeyValidationRequest, config = {}) {
    const requestURL = `${this.integrationsURL}/validate/partition-key`;

    const params = {
      partition_key: value.destinationPartitionKey
    };

    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return courierService.post<GenericApiResponse<{ valid: boolean; reason: string }>>(
      requestURL,
      params,
      options
    );
  },

  disablePipeline(pipelineId: number, cancelRunningJobs: boolean) {
    return courierService.post<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}/actions/disable`,
      {
        should_cancel_running_jobs: cancelRunningJobs
      }
    );
  },

  enablePipeline(pipelineId: number) {
    return courierService.post<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}/actions/enable`,
      {}
    );
  },

  resyncPipeline(pipelineId: number, config: Partial<AxiosRequestConfig> = {}) {
    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };
    return courierService.post<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}/actions/restart`,
      {},
      options
    );
  },

  updateSyncFrequency(
    pipelineId: number,
    syncFrequency: number,
    config: Partial<AxiosRequestConfig> = {}
  ) {
    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return courierService.post<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}/actions/update-sync-frequency`,
      {
        sync_frequency: syncFrequency
      },
      options
    );
  },

  runNow(pipelineId: number, config: Partial<AxiosRequestConfig> = {}) {
    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return courierService.put<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}/actions/run-now`,
      {},
      options
    );
  },

  deletePipeline(pipelineId: number, config: Partial<AxiosRequestConfig> = {}) {
    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return courierService.delete<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}`,
      options
    );
  },

  getIntegrationFeatures(pipelineId: number) {
    return courierService.get<GenericApiResponse<PipelineSettingsFeaturesData>>(
      `${this.integrationsURL}/${pipelineId}/features`
    );
  },

  getIntegrationDisplayConfig(pipelineId: number) {
    return courierService.get<
      GenericApiResponse<{
        source: RawEntityDisplayConfig<RawSourceType>;
        destination: RawEntityDisplayConfig<RawDestinationType>;
        integration: RawEntityDisplayConfig<RawIntegration>;
      }>
    >(`${this.integrationsURL}/${pipelineId}/display-config`);
  },

  editPipeline(pipelineId: number) {
    return courierService.post<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}/actions/edit`,
      {}
    );
  },

  savePipeline(pipelineId: number) {
    return courierService.put<GenericApiResponse<unknown>>(
      `${this.integrationsURL}/${pipelineId}/actions/edit/save`,
      {},
      NETWORK_REQ_OPTIONS_WITH_TOAST
    );
  },

  cancelPipelineEditSave(pipelineId: number) {
    return courierService.put(
      `${this.integrationsURL}/${pipelineId}/actions/edit/cancel`,
      {},
      NETWORK_REQ_OPTIONS_WITH_TOAST
    );
  },

  getPipelineReviewContent(pipelineId: number) {
    return courierService.get<GenericApiResponse<RawPipelineReviewContent>>(
      `${this.integrationsURL}/${pipelineId}/actions/edit/review`
    );
  },

  getPipelineBanners(pipelineId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    return courierService.get<GenericApiResponse<RawIntegrationBannerResponse>>(
      `${this.integrationsURL}/${pipelineId}/banner`,
      options
    );
  },

  submitPipelineReviewSelectionDiffRequest(pipelineId: number) {
    return courierService.put<
      GenericApiResponse<{
        review_page_id: string;
      }>
    >(
      `${this.integrationsURL}/${pipelineId}/actions/edit/review/selections`,
      {},
      DEFAULT_NETWORK_REQ_OPTIONS
    );
  },

  getPipelineReviewSelection(pipelineId: number, reviewPageId: string) {
    return courierService.get<GenericApiResponse<RawPipelineReviewSelectionResponse>>(
      `${this.integrationsURL}/${pipelineId}/actions/edit/review/selections/${reviewPageId}/status`
    );
  },

  upgradePipeline(pipelineId: number) {
    return courierService.post<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/create/migrate`,
      {
        standard_integration_id: pipelineId
      }
    );
  },

  getV1Pipeline(seqId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.upgradeIntegrationURL}/by-seq-id/${seqId}`;

    return courierService.get<GenericApiResponse<StandardPipelineInfo>>(requestURL, options);
  }
};
